import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Empty, 
  Row, 
  Col, 
  Dropdown, 
  Tooltip,
  Space,
  Modal
} from 'antd';
import { 
  PlusOutlined, 
  MoreOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import GraficaMedidor from './GraficaMedidor';
import FormularioGrafica from './FormularioGrafica';
import { useGraficasPersonalizadas } from '../../hooks/useGraficasPersonalizadas';
import './GraficasPersonalizadasWidget.scss';

const { confirm } = Modal;

// Componente para item sorteable
const SortableGraficaItem = ({ 
  grafica, 
  datos, 
  loading, 
  error, 
  onRefresh, 
  onEdit, 
  onDelete
}) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: grafica._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const menuItems = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Editar',
      onClick: () => onEdit(grafica)
    },
    {
      key: 'refresh',
      icon: <ReloadOutlined />,
      label: 'Actualizar datos',
      onClick: () => onRefresh(grafica._id, grafica.tipoGrafica)
    },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Eliminar',
      danger: true,
      onClick: () => onDelete(grafica._id, grafica.titulo)
    }
  ];

  return (
    <div ref={setNodeRef} style={style} className="sortable-grafica-item">
      <div className="sortable-grafica-item__header">
        
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button 
            type="text" 
            icon={<MoreOutlined />} 
            className="sortable-grafica-item__menu"
          />
        </Dropdown>
      </div>
      
      <GraficaMedidor
        grafica={grafica}
        datos={datos}
        loading={loading}
        error={error}
        onRefresh={onRefresh}
        className="sortable-grafica-item__content"
      />
    </div>
  );
};

const GraficasPersonalizadasWidget = () => {
  const {
    loading,
    initialLoad,
    sensoresDisponibles,
    graficasUsuario,
    datosGraficas,
    loadingGraficas,
    erroresGraficas,
    modoDesarrollo,
    showFormulario,
    graficaEditando,
    crearGrafica,
    actualizarGrafica,
    eliminarGrafica,
    toggleActivaGrafica,
    reordenarGraficas,
    refresh,
    refreshGrafica,
    abrirFormulario,
    cerrarFormulario
  } = useGraficasPersonalizadas();

  const [submittingForm, setSubmittingForm] = useState(false);

  // Configurar sensores para drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Manejar reordenamiento
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = graficasUsuario.findIndex(item => item._id === active.id);
      const newIndex = graficasUsuario.findIndex(item => item._id === over.id);
      
      const nuevasGraficas = arrayMove(graficasUsuario, oldIndex, newIndex);
      
      try {
        await reordenarGraficas(nuevasGraficas);
      } catch (error) {
        console.error('Error reordering graficas:', error);
      }
    }
  };

  // Manejar envío del formulario
  const handleSubmitFormulario = async (datosGrafica) => {
    try {
      setSubmittingForm(true);
      
      if (graficaEditando) {
        await actualizarGrafica(graficaEditando._id, datosGrafica);
      } else {
        await crearGrafica(datosGrafica);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    } finally {
      setSubmittingForm(false);
    }
  };

  // Manejar eliminación con confirmación
  const handleEliminar = async (graficaId, titulo) => {
    confirm({
      title: '¿Eliminar gráfica?',
      icon: <ExclamationCircleOutlined />,
      content: `¿Estás seguro de que deseas eliminar la gráfica "${titulo}"? Esta acción no se puede deshacer.`,
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await eliminarGrafica(graficaId);
        } catch (error) {
          console.error('Error deleting grafica:', error);
        }
      }
    });
  };

  // Manejar toggle de estado activo
  const handleToggleActive = async (graficaId, activa) => {
    try {
      await toggleActivaGrafica(graficaId, activa);
    } catch (error) {
      console.error('Error toggling grafica:', error);
    }
  };

  if (initialLoad && loading) {
    return (
      <Card 
        className="graficas-personalizadas-widget"
        title="Gráficas Personalizadas"
        loading={true}
      >
        <div style={{ height: 300 }} />
      </Card>
    );
  }

  return (
    <div className="graficas-personalizadas-widget">
      <Card
        title={
          <div className="graficas-personalizadas-widget__header">
            <span>Gráficas Personalizadas</span>
            {modoDesarrollo && (
              <Tooltip title="Modo desarrollo - Usando datos de prueba">
                <span className="graficas-personalizadas-widget__dev-badge">DEV</span>
              </Tooltip>
            )}
          </div>
        }
        extra={
          <Space>
            <Tooltip title="Actualizar todas las gráficas">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={refresh}
                loading={loading}
                size="small"
              />
            </Tooltip>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => abrirFormulario()}
              size="small"
            >
              Nuevo Medidor
            </Button>
          </Space>
        }
        className="graficas-personalizadas-widget__card"
      >
        {graficasUsuario.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p>No tienes gráficas personalizadas</p>
                <p style={{ color: '#666', fontSize: '14px' }}>
                  Crea tu primera gráfica para visualizar los datos de tus sensores
                </p>
              </div>
            }
          >
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => abrirFormulario()}
            >
              Crear Primera Gráfica
            </Button>
          </Empty>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={graficasUsuario.map(g => g._id)}
              strategy={verticalListSortingStrategy}
            >
              <Row gutter={[16, 16]}>
                {graficasUsuario.map((grafica) => (
                  <Col 
                    key={grafica._id} 
                    xs={24} 
                    lg={12} 
                    xl={8}
                  >
                    <SortableGraficaItem
                      grafica={grafica}
                      datos={datosGraficas[grafica._id]}
                      loading={loadingGraficas[grafica._id]}
                      error={erroresGraficas[grafica._id]}
                      onRefresh={refreshGrafica}
                      onEdit={abrirFormulario}
                      onDelete={handleEliminar}
                      onToggleActive={handleToggleActive}
                    />
                  </Col>
                ))}
              </Row>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {/* Formulario de creación/edición */}
      <FormularioGrafica
        visible={showFormulario}
        onCancel={cerrarFormulario}
        onSubmit={handleSubmitFormulario}
        grafica={graficaEditando}
        sensoresDisponibles={sensoresDisponibles}
        loading={submittingForm}
        loadingSensores={loading}
      />
    </div>
  );
};

export default GraficasPersonalizadasWidget;